// Theme settings persist to BOTH localStorage (instant) and database (cross-device).
// localStorage: applied on page load before React hydration (no flash).
// Database: synced via use-preferences.ts hook on login (fills empty localStorage).
// When adding new theme settings, update syncThemeToServer() in use-preferences.ts.
import { create } from 'zustand';
import {
  applyTheme,
  applyFontSize,
  applyCompactMode,
  type ThemePresetId,
  type ThemeMode
} from '../lib/theme-engine';
import { syncThemeToServer } from '../hooks/use-preferences';

interface ThemeState {
  mode: ThemeMode;
  preset: ThemePresetId;
  customColor: string | null;
  fontSize: 'small' | 'default' | 'large';
  compact: boolean;
  setMode: (mode: ThemeMode) => void;
  setPreset: (preset: ThemePresetId) => void;
  setCustomColor: (hex: string | null) => void;
  setFontSize: (size: 'small' | 'default' | 'large') => void;
  setCompact: (compact: boolean) => void;
}

function loadFromStorage(): {
  mode: ThemeMode;
  preset: ThemePresetId;
  customColor: string | null;
  fontSize: 'small' | 'default' | 'large';
  compact: boolean;
} {
  if (typeof window === 'undefined') {
    return {
      mode: 'light',
      preset: 'default',
      customColor: null,
      fontSize: 'default',
      compact: false
    };
  }
  return {
    mode: (localStorage.getItem('eligaro-theme-mode') as ThemeMode) || 'light',
    preset: (localStorage.getItem('eligaro-theme-preset') as ThemePresetId) || 'default',
    customColor: localStorage.getItem('eligaro-theme-custom') || null,
    fontSize:
      (localStorage.getItem('eligaro-font-size') as 'small' | 'default' | 'large') || 'default',
    compact: localStorage.getItem('eligaro-compact') === 'true'
  };
}

function saveAndApply(
  mode: ThemeMode,
  preset: ThemePresetId,
  customColor: string | null,
  fontSize: 'small' | 'default' | 'large',
  compact: boolean
): void {
  localStorage.setItem('eligaro-theme-mode', mode);
  localStorage.setItem('eligaro-theme-preset', preset);
  if (customColor) {
    localStorage.setItem('eligaro-theme-custom', customColor);
  } else {
    localStorage.removeItem('eligaro-theme-custom');
  }
  applyTheme(preset, customColor ?? undefined, mode);
  syncThemeToServer(mode, preset, customColor, fontSize, compact);
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const initial = loadFromStorage();
  return {
    ...initial,
    setMode: (mode) => {
      const { preset, customColor, fontSize, compact } = get();
      saveAndApply(mode, preset, customColor, fontSize, compact);
      set({ mode });
    },
    setPreset: (preset) => {
      const { mode, fontSize, compact } = get();
      saveAndApply(mode, preset, null, fontSize, compact);
      set({ preset, customColor: null });
    },
    setCustomColor: (hex) => {
      const { mode, preset, fontSize, compact } = get();
      saveAndApply(mode, preset, hex, fontSize, compact);
      set({ customColor: hex });
    },
    setFontSize: (size) => {
      localStorage.setItem('eligaro-font-size', size);
      applyFontSize(size);
      const { mode, preset, customColor, compact } = get();
      syncThemeToServer(mode, preset, customColor, size, compact);
      set({ fontSize: size });
    },
    setCompact: (compact) => {
      localStorage.setItem('eligaro-compact', String(compact));
      applyCompactMode(compact);
      const { mode, preset, customColor, fontSize } = get();
      syncThemeToServer(mode, preset, customColor, fontSize, compact);
      set({ compact });
    }
  };
});

// Initialize on load
if (typeof window !== 'undefined') {
  const { mode, preset, customColor, fontSize, compact } = loadFromStorage();
  applyTheme(preset, customColor ?? undefined, mode);
  applyFontSize(fontSize);
  applyCompactMode(compact);
}
